import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import KarteInterface, {
  KarteCommentInterface,
} from "../../interfaces/KarteInterface";

const path = "/api/v1/students/{student_id}/kartes" as const;

const buildQueryKey = (studentId: string) => [path, studentId];

export const useFetchStudentKartes = ({ studentId }: { studentId: string }) => {
  const result = useInfiniteQuery<ApiResponse<KarteInterface[]>, Error>({
    queryKey: buildQueryKey(studentId),
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(path, {
        params: {
          path: { student_id: studentId },
          query: {
            page: pageParam,
          },
        },
      });
      if (response.ok && data) {
        return data.kartes;
      }
      throw await createError(response);
    },
    getNextPageParam,
    initialPageParam: 1,
  });

  const kartes = result.data?.pages.flatMap((page) => page.data || []);

  const loadMore = () => {
    if (result.hasNextPage && !result.isFetchingNextPage) {
      result.fetchNextPage();
    }
  };

  return {
    ...result,
    kartes,
    loadMore,
  };
};

export const useUpdateStudentKartesCache = () => {
  const queryClient = useQueryClient();

  // 新しいKarteをキャッシュに追加
  const addKarteCache = (studentId: string, karte: KarteInterface) => {
    const queryKey = buildQueryKey(studentId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        const pages = [...oldData.pages];
        pages[0] = {
          ...pages[0],
          data: [karte, ...(pages[0]?.data || [])],
        };

        return {
          ...oldData,
          pages,
        };
      },
    );
  };

  // 指定IDのKarteのキャッシュを更新
  const updateKarteCache = (
    studentId: string,
    updatedKarte: KarteInterface,
  ) => {
    const queryKey = buildQueryKey(studentId);
    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) =>
              karte.id === updatedKarte.id ? updatedKarte : karte,
            ),
          })),
        };
      },
    );
  };

  // 指定IDのKarteをキャッシュから削除
  const removeKarteFromCache = (studentId: string, karteId: string) => {
    const queryKey = buildQueryKey(studentId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((karte) => karte.id !== karteId),
          })),
        };
      },
    );
  };

  // 指定IDのKarteにコメントを追加
  const addKarteCommentCache = (
    studentId: string,
    karteId: string,
    karteComment: KarteCommentInterface,
  ) => {
    const queryKey = buildQueryKey(studentId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) => {
              // カルテIDが一致する場合はコメントを末尾に追加
              if (karte.id === karteId) {
                return {
                  ...karte,
                  comments: [...karte.comments, karteComment],
                };
              }
              return karte;
            }),
          })),
        };
      },
    );
  };

  // 指定IDのKarteのコメントを削除
  const removeKarteCommentCache = (
    studentId: string,
    karteId: string,
    karteCommentId: string,
  ) => {
    const queryKey = buildQueryKey(studentId);

    queryClient.setQueriesData<InfiniteData<ApiResponse<KarteInterface[]>>>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((karte) =>
              karte.id === karteId
                ? {
                    ...karte,
                    comments: karte.comments.filter(
                      (comment) => comment.id !== karteCommentId,
                    ),
                  }
                : karte,
            ),
          })),
        };
      },
    );
  };

  const refetchStudentKartes = (studentId: string) => {
    const queryKey = buildQueryKey(studentId);
    queryClient.resetQueries({ queryKey });
    queryClient.refetchQueries({ queryKey });
  };

  return {
    addKarteCache,
    updateKarteCache,
    removeKarteFromCache,
    addKarteCommentCache,
    removeKarteCommentCache,
    refetchStudentKartes,
  };
};
